/**
 * @file SortableBlockRow.tsx
 * @description Строка блока в структуре конструктора (phase19.8 → 19.16):
 * ручка перетаскивания (dnd-kit), название секции и строка «1 колонка ·
 * 3 элемента», значок ◌ у прозрачной секции («виден фон страницы»),
 * переключатель «Показывать блок» (зона нажатия 48) и меню «⋯».
 * `readOnly` — только просмотр (места блога на экране «Блог»).
 *
 * phase19.16 Р2 «чистка» (TD-327, SB107): прежних типов больше нет. Блок
 * другого типа из данных — строка «Неподдерживаемый блок — удалите» с
 * кнопкой удаления, без формы и без падения.
 *
 * phase19.16 Р2а: в шаблоне страницы товара блок с обязательными
 * элементами скрыть нельзя — переключатель неактивен с причиной
 * (`toggleLocked`); вместо строки «колонки · элементы» — элементы блока.
 */

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { CircleAlert, GripVertical, Trash2 } from "lucide-react";

import type { SitePageBlock } from "../../lib/api-site-pages";
import { RowActionsMenu, type RowAction } from "../ui/RowActionsMenu";
import { Switch } from "../ui/switch";
import { blockSummary, blockTitle, isSectionBlock } from "./block-meta";
import { cn } from "@/lib/utils";

interface SortableBlockRowProps {
  block: SitePageBlock;
  isSelected: boolean;
  onSelect: (block: SitePageBlock) => void;
  onToggle: (block: SitePageBlock, isEnabled: boolean) => void;
  /** Удалить (неподдерживаемый блок — единственное действие). */
  onDelete?: (block: SitePageBlock) => void;
  /** Меню «⋯» действий с блоком. */
  actions?: RowAction[];
  /** Только просмотр: без ручки и переключателя. */
  readOnly?: boolean;
  /** Почему блок нельзя скрыть (переключатель неактивен); `null` — можно. */
  toggleLocked?: string | null;
  /** Р3а: метка черновика «изменён» / «новый» (uiux-r3 §3.1); нет — без метки. */
  mark?: string | null;
}

function UnsupportedRow({ block, onDelete, readOnly }: Pick<SortableBlockRowProps, "block" | "onDelete" | "readOnly">) {
  const { setNodeRef, transform, transition } = useSortable({ id: block.id });
  return (
    <li ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }} className="flex min-h-12 items-center gap-2 rounded-md bg-surface-lowest px-2 [box-shadow:inset_0_0_0_1px_var(--outline-variant)]">
      <CircleAlert aria-hidden className="size-4 shrink-0 text-warning" />
      <span className="type-body min-w-0 flex-1 truncate text-on-surface-variant">{blockTitle(block)}</span>
      {readOnly || !onDelete ? null : (
        <button type="button" onClick={() => onDelete(block)} className="inline-flex size-12 shrink-0 items-center justify-center text-on-surface-variant hover:text-destructive" aria-label="Удалить неподдерживаемый блок">
          <Trash2 className="size-4" />
        </button>
      )}
    </li>
  );
}

export function SortableBlockRow({ block, isSelected, onSelect, onToggle, onDelete, actions, readOnly = false, toggleLocked = null, mark = null }: SortableBlockRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
  if (!isSectionBlock(block)) return <UnsupportedRow block={block} onDelete={onDelete} readOnly={readOnly} />;
  const transparent = block.payload.look.bg.kind === "transparent";

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "flex min-h-12 items-center gap-1.5 rounded-md px-1 py-2 max-xl:py-0",
        isDragging && "relative z-10 opacity-60 shadow-lg",
        isSelected ? "bg-primary/10" : "bg-surface-lowest hover:bg-surface-low",
        !block.isEnabled && "opacity-60",
      )}
    >
      {readOnly ? null : (
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="inline-flex shrink-0 cursor-grab touch-none items-center justify-center p-1.5 text-on-surface-muted max-xl:min-h-12 max-xl:min-w-12"
          aria-label="Перетащить блок"
        >
          <GripVertical className="h-4 w-4" />
        </button>
      )}
      <button type="button" onClick={() => onSelect(block)} className="flex min-w-0 flex-1 flex-col justify-center text-left max-xl:min-h-12">
        <div className="flex min-w-0 items-center gap-1.5 text-sm font-medium">
          <span className="truncate" title={blockTitle(block)}>
            {blockTitle(block)}
          </span>
          {transparent ? (
            <span className="type-caption shrink-0 text-on-surface-variant" title="Прозрачный — виден фон страницы" aria-label="Прозрачный — виден фон страницы">
              ◌
            </span>
          ) : null}
          {mark ? <span className="type-caption shrink-0 font-normal text-warning">● {mark}</span> : null}
        </div>
        <div className="line-clamp-2 text-xs text-on-surface-muted">{blockSummary(block)}</div>
      </button>
      {readOnly ? null : (
        // Область касания 48: подпись-обёртка передаёт нажатие переключателю.
        <label title={toggleLocked ?? undefined} className="inline-flex min-h-12 min-w-12 shrink-0 cursor-pointer items-center justify-center has-[:disabled]:cursor-not-allowed">
          <Switch
            checked={block.isEnabled}
            disabled={toggleLocked !== null && block.isEnabled}
            onCheckedChange={(checked) => onToggle(block, checked === true)}
            aria-label={toggleLocked && block.isEnabled ? `Показывать блок — ${toggleLocked}` : "Показывать блок"}
          />
        </label>
      )}
      {actions && actions.length > 0 ? <RowActionsMenu actions={actions} title={`Блок «${blockTitle(block)}»`} size="compact" /> : null}
    </li>
  );
}
